import React, { useState, useEffect } from 'react';
import { Download, Search, ChevronUp, ChevronDown } from 'lucide-react';
import type ExcelJS from 'exceljs';
import { exportSimpleList } from '../utils/excelHelper';
import * as api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import AvatarEditor from '../components/AvatarEditor';
import CsvImportModal from '../components/CsvImportModal';
import PasswordInput from '../components/PasswordInput';
import PasswordReveal, { canManagePasswordOf } from '../components/PasswordReveal';
import MasterLinkButton from '../components/MasterLinkButton';
import { usePager } from '../components/TableTools';
import { SmartDateInput } from '../components/SmartDateTime';
import TalentDetailDrawer from '../components/TalentDetailDrawer';

const USER_EXPORT_HEADER = ['社員コード', '氏名', 'ロール', 'メール', '登録日'];
const USER_TMPL_HEADER   = ['社員コード', '氏名', 'パスワード', 'ロール', 'メール'];
const USER_TMPL_ROWS     = [
  ['EMP001', '山田 太郎', 'password123', 'crew',        'yamada@example.com'],
  ['EMP002', '鈴木 次郎', 'password123', 'captain',     ''],
  ['EMP003', '田中 花子', 'password123', 'shore_admin', 'tanaka@example.com'],
];
const USER_ROLE_LABELS: Record<string, string> = {
  crew: '船員', captain: '船長', admin: '管理者', shore_admin: '陸上管理者', system_admin: 'システム管理者',
};

function userToCsvRow(u: { employee_code: string; user_name: string; user_role: string; email?: string; created_at: string }): string[] {
  return [u.employee_code, u.user_name, USER_ROLE_LABELS[u.user_role] ?? u.user_role, u.email ?? '', u.created_at?.slice(0, 10) ?? ''];
}
function exportCsvFile(rows: string[][], header: string[], filename: string) {
  const csv = [header, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob); const a = document.createElement('a');
  a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}
async function exportExcelFile(rows: string[][], header: string[], filename: string) {
  const colWidths = [16, 20, 16, 28, 14];
  const headers = header.map((label, i) => ({ label, width: colWidths[i] ?? 16 }));
  await exportSimpleList(headers, rows, 'データ', filename, 'ユーザー一覧');
}

interface User {
  id: string;
  employee_code: string;
  user_name: string;
  user_role: string;
  company_id: string;
  email?: string;
  avatar_path?: string | null;
  created_at: string;
  is_crew_eligible?: number;
  name_kana?: string | null;
  birth_date?: string | null;
  address?: string | null;
  license_no?: string | null;
  license_grade?: string | null;
  /** 辞令上の役職（会社としての立場）。乗船中に執る職は配乗側で設定する */
  rank_position?: string | null;
  license_expiry_date?: string | null;
  seaman_book_expiry_date?: string | null;
  license_pdf_path?: string | null;
  license_image_path?: string | null;
  seaman_book_pdf_path?: string | null;
  seaman_book_image_path?: string | null;
  license_pdf_name?: string | null;
  license_image_name?: string | null;
  seaman_book_pdf_name?: string | null;
  seaman_book_image_name?: string | null;
  nationality?: string | null;
  birth_place?: string | null;
  seaman_book_no?: string | null;
  crew_note?: string | null;
  face_registered_at?: string | null;
  /** 管理者が設定したパスワードを保管しているか（1なら管理画面で表示できる） */
  has_managed_password?: number;
  /** パスワードを最後に変更した日時（UTC）。未記録なら導入前から変わっていない */
  password_changed_at?: string | null;
  /** 誰が変更したか（本人／管理者名／ユニゾン（運営）） */
  password_changed_by?: string | null;
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';

/** ユーザーのアイコン画像をアップロード（登録/編集フォームから使用） */
async function uploadUserAvatar(userId: string, file: File): Promise<void> {
  const fd = new FormData();
  fd.append('avatar', file);
  const r = await fetch(`${API_BASE}/users/${userId}/avatar`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${api.getToken() ?? ''}`, 'X-Company-Subdomain': window.location.hostname.split('.')[0] ?? 'unison-tech' },
    body: fd,
  });
  if (!r.ok) throw new Error('avatar upload failed');
}

// 現在保存されている書類を認証付きで表示・ダウンロードする
function viewStoredDoc(userId: string, docType: string) {
  const token = api.getToken() ?? '';
  const subdomain = (() => { const h = window.location.hostname; if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; } return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech'; })();
  fetch(`${API_BASE}/licenses/${userId}/${docType}`, { headers: { Authorization: `Bearer ${token}`, 'X-Company-Subdomain': subdomain, 'X-App-Type': 'admin' } })
    .then(r => r.ok ? r.blob() : Promise.reject())
    .then(b => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = `${docType}_${userId}.${docType.includes('pdf') ? 'pdf' : 'jpg'}`;
      a.target = '_blank'; a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch(() => alert('表示に失敗しました'));
}

// ── 書類アップロードボックス ────────────────────────────────────────────────
// 削除/差し替えを押すと、件名を出したまま「削除(差し替え)／取消」の確認ボタンを表示し、
// その場で確定する（モーダルの「更新」は不要）。取消で元のファイルをそのまま維持できる。
const DocUploadBox: React.FC<{
  label: string;
  icon: string;
  userId: string;
  docType: string;             // 'pdf' | 'image' | 'seaman-book-pdf' | 'seaman-book-image'
  accept: string;
  hasFile: boolean;            // サーバに保存済みか
  currentName?: string | null; // 保存済みの元ファイル名（件名）
  onChanged: () => void;       // 反映後に一覧/編集対象を最新化
}> = ({ label, icon, userId, docType, accept, hasFile, currentName, onChanged }) => {
  const [busy, setBusy] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [pendingFile, setPendingFile] = React.useState<File | null>(null); // 差し替え確認中の新ファイル
  const isPdf = docType.includes('pdf');
  const authHeaders = { Authorization: `Bearer ${api.getToken() ?? ''}`, 'X-Company-Subdomain': window.location.hostname.split('.')[0] ?? 'unison-tech' };
  const titleName = currentName || (isPdf ? '登録済みPDF' : '登録済み画像');

  const doUpload = async (file: File) => {
    setBusy(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await fetch(`${API_BASE}/licenses/${userId}/${docType}`, { method: 'POST', headers: authHeaders, body: fd });
      if (!r.ok) throw new Error();
      onChanged();
    } catch { alert('アップロードに失敗しました'); }
    setBusy(false); setPendingFile(null);
  };
  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/licenses/${userId}/${docType}`, { method: 'DELETE', headers: authHeaders });
      if (!r.ok) throw new Error();
      onChanged();
    } catch { alert('削除に失敗しました'); }
    setBusy(false); setConfirmDelete(false);
  };

  return (
    <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>{icon} {label}</div>

      {pendingFile ? (
        /* 差し替え確認：新ファイルの件名 + 差し替え/取消 */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#b45309', fontWeight: 700, wordBreak: 'break-all' }}>🆕 {pendingFile.name}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button disabled={busy} onClick={() => doUpload(pendingFile)}
              style={{ fontSize: 11, color: '#fff', background: '#0369a1', border: 'none', borderRadius: 4, padding: '3px 12px', cursor: busy ? 'wait' : 'pointer', fontWeight: 700 }}>
              {busy ? '差し替え中...' : '差し替え'}
            </button>
            <button disabled={busy} onClick={() => setPendingFile(null)}
              style={{ fontSize: 11, color: '#64748b', background: 'none', border: '1px solid #cbd5e1', borderRadius: 4, padding: '3px 12px', cursor: 'pointer' }}>取消</button>
          </div>
        </div>
      ) : confirmDelete ? (
        /* 削除確認：件名を出したまま 削除/取消 */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#0f172a', fontWeight: 700, wordBreak: 'break-all' }}>{isPdf ? '📄' : '🖼️'} {titleName}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: '#991b1b' }}>削除しますか？</span>
            <button disabled={busy} onClick={doDelete}
              style={{ fontSize: 11, color: '#fff', background: '#ef4444', border: 'none', borderRadius: 4, padding: '3px 12px', cursor: busy ? 'wait' : 'pointer', fontWeight: 700 }}>
              {busy ? '削除中...' : '削除'}
            </button>
            <button disabled={busy} onClick={() => setConfirmDelete(false)}
              style={{ fontSize: 11, color: '#64748b', background: 'none', border: '1px solid #cbd5e1', borderRadius: 4, padding: '3px 12px', cursor: 'pointer' }}>取消</button>
          </div>
        </div>
      ) : hasFile ? (
        /* 保存済み（件名表示 + 表示/削除/差し替え） */
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => viewStoredDoc(userId, docType)} title={currentName ?? ''}
            style={{ fontSize: 12, color: '#0066cc', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0, maxWidth: '100%', wordBreak: 'break-all', textAlign: 'left' }}>
            {isPdf ? '📄' : '🖼️'} {currentName || (isPdf ? '表示・ダウンロード' : '画像を表示')}
          </button>
          <button onClick={() => setConfirmDelete(true)}
            style={{ fontSize: 11, color: '#ef4444', background: 'none', border: '1px solid #fca5a5', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>削除</button>
          <label style={{ fontSize: 11, color: '#0369a1', background: '#e0f2fe', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>
            差し替え
            <input type="file" accept={accept} style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) setPendingFile(f); e.target.value = ''; }} />
          </label>
        </div>
      ) : (
        /* 未登録 */
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#0369a1',
          background: '#e0f2fe', border: '1.5px dashed #7dd3fc', borderRadius: 6, padding: '6px 12px', cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'アップロード中...' : '+ ファイルを選択'}
          <input type="file" accept={accept} style={{ display: 'none' }} disabled={busy}
            onChange={e => { const f = e.target.files?.[0]; if (f) doUpload(f); e.target.value = ''; }} />
        </label>
      )}
    </div>
  );
};

// ── 書類セクション（PDF + 写真の2枠）──────────────────────────────────────
const DocSection: React.FC<{
  title: string;
  userId: string;
  pdfType: string;
  imageType: string;
  hasPdf: boolean;
  hasImage: boolean;
  pdfName?: string | null;
  imageName?: string | null;
  onChanged: () => void;
}> = ({ title, userId, pdfType, imageType, hasPdf, hasImage, pdfName, imageName, onChanged }) => (
  <div style={{ marginTop: 14, padding: '12px 14px', background: '#fff', borderRadius: 10, border: '1.5px solid #e2e8f0' }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 10 }}>{title}</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      <DocUploadBox label="PDF（スキャン原本）" icon="📄" userId={userId}
        docType={pdfType} accept=".pdf" hasFile={hasPdf} currentName={pdfName} onChanged={onChanged} />
      <DocUploadBox label="写真（画像ファイル）" icon="🖼️" userId={userId}
        docType={imageType} accept="image/*" hasFile={hasImage} currentName={imageName} onChanged={onChanged} />
    </div>
  </div>
);

const ROLE_LABELS: Record<string, string> = {
  crew: '船員',
  captain: '船長',
  admin: '管理者',
  shore_admin: '陸上管理者',
  system_admin: 'システム管理者',
};

const ROLE_COLORS: Record<string, { background: string; color: string }> = {
  system_admin: { background: '#ede9fe', color: '#7c3aed' },
  shore_admin:  { background: '#dbeafe', color: '#2563eb' },
  admin:        { background: '#dbeafe', color: '#2563eb' },
  captain:      { background: '#fef3c7', color: '#d97706' },
  crew:         { background: '#f1f5f9', color: '#475569' },
};

// ── 陸上管理者の機能別編集権限モーダル ────────────────────────────────────
// システム管理者のみが開ける。オフにした機能は該当ユーザーが閲覧のみになる（保存・削除等がAPIで拒否される）
function EditPermissionsModal({ user, onClose }: { user: User; onClose: () => void }) {
  const [features, setFeatures] = useState<{ key: string; label: string }[]>([]);
  const [perms, setPerms] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [f, p] = await Promise.all([
          api.get<any>('/admin-permissions/features'),
          api.get<any>(`/admin-permissions/${user.id}`),
        ]);
        setFeatures(f?.data ?? []);
        setPerms(p?.data ?? {});
        setLoading(false);
      } catch (e: any) {
        alert(e.message ?? '権限の取得に失敗しました');
        onClose();
      }
    })();
  }, [user.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin-permissions/${user.id}`, { permissions: perms });
      onClose();
    } catch (e: any) { alert(e.message ?? '保存に失敗しました'); }
    setSaving(false);
  };

  const allOn = features.every(f => perms[f.key] !== false);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500, width: '95%', maxHeight: '85vh', overflowY: 'auto' }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>編集権限の設定</h2>
        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px' }}>
          <strong>{user.user_name}</strong>（陸上管理者）が編集できる機能を選択します。オフにした機能は<strong>閲覧のみ</strong>になります。
        </p>
        {loading ? <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div> : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <button onClick={() => { const next: Record<string, boolean> = {}; features.forEach(f => { next[f.key] = !allOn; }); setPerms(next); }}
                style={{ fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                {allOn ? 'すべてオフ' : 'すべてオン'}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {features.map(f => {
                const on = perms[f.key] !== false;
                return (
                  <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, border: `1.5px solid ${on ? '#bfdbfe' : '#e2e8f0'}`, background: on ? '#eff6ff' : '#f8fafc', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: on ? '#1d4ed8' : '#94a3b8' }}>
                    <input type="checkbox" checked={on} onChange={e => setPerms(prev => ({ ...prev, [f.key]: e.target.checked }))} style={{ accentColor: '#2563eb', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{f.label}</span>
                    {!on && <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', flexShrink: 0 }}>閲覧のみ</span>}
                  </label>
                );
              })}
            </div>
            <div className="flex-row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn-o" onClick={onClose}>キャンセル</button>
              <button className="btn-p" onClick={save} disabled={saving}>{saving ? '保存中…' : '保存'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const UsersPage: React.FC = () => {
  const { user: authUser } = useAuth();
  const [permTarget, setPermTarget] = useState<User | null>(null);
  // 削除可否: 運営管理者代理は誰でも可。通常セッションは自分より下位のロールのみ
  // （同順位・上位は不可。システム管理者は運営管理者のみ削除可）
  const ROLE_RANK_UI: Record<string, number> = { system_admin: 4, shore_admin: 3, captain: 2, crew: 1 };
  const myRank = ROLE_RANK_UI[authUser?.user_role ?? ''] ?? 0;
  const canDeleteUser = (u: User): boolean =>
    !!authUser?.is_super_admin || (u.user_role !== 'system_admin' && (ROLE_RANK_UI[u.user_role] ?? 0) < myRank);
  const [users, setUsers]           = useState<User[]>([]);
  const [loading, setLoading]       = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [editTarget, setEditTarget] = useState<User | null>(null);
  // 管理者が設定したパスワードの表示（一覧の行ごとに一時的に見せる）。
  // 2026-10-02: 「パスワード再発行」は廃止し、変更は編集画面に一本化。
  const [pwShown, setPwShown] = useState<Record<string, string>>({});
  const [pwLoading, setPwLoading] = useState<string | null>(null);
  // パスワードを保存したら編集画面の表示欄を伏せ字に戻すためのキー
  const [pwSavedTick, setPwSavedTick] = useState(0);
  const [form, setForm]             = useState({ employee_code: '', user_name: '', email: '', role: 'crew', password: '', name_kana: '', birth_date: '', address: '', license_no: '', license_grade: '', license_expiry_date: '', seaman_book_expiry_date: '', is_crew_eligible: true, nationality: '', birth_place: '', seaman_book_no: '', crew_note: '', rank_position: '' });
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [faceDeleteConfirm, setFaceDeleteConfirm] = useState(false);
  const [faceDeleting, setFaceDeleting] = useState(false);
  const [error, setError]           = useState('');
  const [iconFile, setIconFile]     = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<User | null>(null);
  const [showImport,     setShowImport]     = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField]     = useState('employee_code');
  const [sortDir, setSortDir]         = useState<'asc' | 'desc'>('asc');
  // 辞令上の役職の入力候補（役職マスタ）
  const [positionOptions, setPositionOptions] = useState<string[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get<{ data: Array<{ name: string; is_active: number }> }>('/position-masters');
        setPositionOptions((r.data ?? []).filter(x => x.is_active !== 0).map(x => x.name));
      } catch { /* マスタ未整備なら候補なしで自由入力 */ }
    })();
  }, []);

  const handleDownload = async (url: string, filename: string) => {
    const token = api.getToken() ?? '';
    const subdomain = (() => { const h = window.location.hostname; if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; } return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech'; })();
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}`, 'X-Company-Subdomain': subdomain, 'X-App-Type': 'admin' } });
      if (!res.ok) throw new Error('ダウンロードに失敗しました');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e: any) { alert(e.message ?? 'ダウンロードに失敗しました'); }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get<any>('/users');
      setUsers(res?.users ?? res?.data ?? []);
    } catch {
      setUsers([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  // 書類の反映後、一覧と編集中ユーザー（モーダル）の両方を最新化して件名等を即反映する
  const refreshAfterUpload = async () => {
    fetchUsers();
    if (!editTarget) return;
    try {
      const r = await api.get<any>('/users/' + editTarget.id);
      if (r?.data) setEditTarget(r.data as User);
    } catch { /* 取得失敗時は一覧更新のみ */ }
  };

  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (field: string) => sortField === field
    ? (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
    : <ChevronUp size={12} style={{ opacity: 0.3 }} />;

  const filtered = users
    .filter(u => {
      const roleMatch = roleFilter ? u.user_role === roleFilter : true;
      if (!searchQuery.trim()) return roleMatch;
      const q = searchQuery.toLowerCase();
      const textMatch = u.user_name.toLowerCase().includes(q) ||
        u.employee_code.toLowerCase().includes(q) ||
        (u.email ?? '').toLowerCase().includes(q);
      return roleMatch && textMatch;
    })
    .sort((a, b) => {
      const va = String((a as any)[sortField] ?? '');
      const vb = String((b as any)[sortField] ?? '');
      const cmp = va.localeCompare(vb, 'ja');
      return sortDir === 'asc' ? cmp : -cmp;
    });

  // フィルタ・ソート適用後の配列をページング（CSV/Excel出力は filtered 全件のまま）
  const pager = usePager(filtered, { noun: '名' });

  const openCreate = () => {
    setEditTarget(null);
    setForm({ employee_code: '', user_name: '', email: '', role: 'crew', password: '', name_kana: '', birth_date: '', address: '', license_no: '', license_grade: '', license_expiry_date: '', seaman_book_expiry_date: '', is_crew_eligible: true, nationality: '', birth_place: '', seaman_book_no: '', crew_note: '', rank_position: '' });
    setError(''); setIconFile(null); setIconPreview('');
    setShowModal(true);
  };

  /**
   * 管理者が設定したパスワードを表示する。
   * 認証用のハッシュからは復元できないため、管理者が設定した分だけ暗号化して
   * 保管しており、それを復号して見せている（閲覧は監査ログに残る）。
   * 本人が自分で変更した場合は保管が無いので表示できない。
   */
  const showPassword = async (u: User) => {
    if (pwShown[u.id]) { setPwShown(p => { const n = { ...p }; delete n[u.id]; return n; }); return; }
    setPwLoading(u.id);
    try {
      const r = await api.get<{ data: { password: string | null; reason?: string } }>(`/users/${u.id}/password`);
      const d = r.data;
      if (d?.password) {
        setPwShown(p => ({ ...p, [u.id]: d.password as string }));
        // 出したままにしない（画面を離れず放置される事故を防ぐ）
        window.setTimeout(() => setPwShown(p => { const n = { ...p }; delete n[u.id]; return n; }), 60000);
      } else {
        alert(`${d?.reason ?? 'このユーザーのパスワードは表示できません。'}\n\n必要な場合は「編集」で新しいパスワードを設定して、本人にお伝えください。`);
      }
    } catch (e: any) {
      alert(e?.message ?? 'パスワードの取得に失敗しました');
    } finally { setPwLoading(null); }
  };

  const openEdit = (u: User) => {
    setEditTarget(u);
    setFaceDeleteConfirm(false);
    const defaultCrewEligible = u.is_crew_eligible !== undefined
      ? !!u.is_crew_eligible
      : ['crew', 'captain'].includes(u.user_role);
    setForm({ employee_code: u.employee_code, user_name: u.user_name, email: u.email ?? '', role: u.user_role, password: '', name_kana: u.name_kana ?? '', birth_date: u.birth_date ?? '', address: u.address ?? '', license_no: u.license_no ?? '', license_grade: u.license_grade ?? '', license_expiry_date: u.license_expiry_date ?? '', seaman_book_expiry_date: u.seaman_book_expiry_date ?? '', is_crew_eligible: defaultCrewEligible, nationality: u.nationality ?? '', birth_place: u.birth_place ?? '', seaman_book_no: u.seaman_book_no ?? '', crew_note: u.crew_note ?? '', rank_position: u.rank_position ?? '' });
    setError(''); setIconFile(null); setIconPreview('');
    setShowModal(true);
  };

  const save = async () => {
    setError('');
    if (form.password && form.password.length < 6) { setError('パスワードは6文字以上にしてください'); return; }
    if (editTarget) {
      if (!form.user_name) { setError('名前を入力してください'); return; }
      try {
        // captain は乗組員配置で管理されるため user_role を送らない（誤って降格しないよう保護）
      const isEditingCaptain = editTarget.user_role === 'captain';
      await api.put(`/users/${editTarget.id}`, {
          user_name: form.user_name,
          email: form.email || undefined,
          ...(isEditingCaptain ? {} : { user_role: form.role }),
          is_crew_eligible: form.is_crew_eligible,
          ...(form.password ? { password: form.password } : {}),
          name_kana: form.name_kana,
          birth_date: form.birth_date,
          address: form.address,
          license_no: form.license_no,
          license_grade: form.license_grade,
          license_expiry_date: form.license_expiry_date,
          seaman_book_expiry_date: form.seaman_book_expiry_date,
          nationality: form.nationality,
          birth_place: form.birth_place,
          seaman_book_no: form.seaman_book_no,
          crew_note: form.crew_note,
          rank_position: form.rank_position,
        });
        if (iconFile) { try { await uploadUserAvatar(editTarget.id, iconFile); } catch { /* 画像だけ失敗しても本体は保存済み */ } }
        if (form.password) { setPwSavedTick(t => t + 1); setPwShown(p => { const n = { ...p }; delete n[editTarget.id]; return n; }); }
        setShowModal(false);
        setIconFile(null); setIconPreview('');
        fetchUsers();
      } catch (e: any) {
        setError(e.message ?? '更新に失敗しました');
      }
    } else {
      if (!form.employee_code || !form.user_name || !form.password) {
        setError('社員コード・名前・パスワードは必須です');
        return;
      }
      try {
        const res = await api.post<any>('/users', { ...form, user_role: form.role, company_id: authUser?.company_id ?? '', is_crew_eligible: form.is_crew_eligible });
        const created = res?.data;
        // 作成フォームで選んだアイコン画像を即アップロード
        if (created?.id && iconFile) { try { await uploadUserAvatar(created.id, iconFile); } catch { /* 画像失敗は無視 */ } }
        setIconFile(null); setIconPreview('');
        fetchUsers();
        if (created?.id) {
          // 登録完了後、そのままアップロードステップへ移行
          setEditTarget({
            id: created.id,
            employee_code: created.employee_code ?? form.employee_code,
            user_name: created.user_name ?? form.user_name,
            user_role: created.user_role ?? form.role,
            company_id: authUser?.company_id ?? '',
            email: form.email || undefined,
            name_kana: form.name_kana || null,
            birth_date: form.birth_date || null,
            address: form.address || null,
            license_no: form.license_no || null,
            license_grade: form.license_grade || null,
            created_at: new Date().toISOString(),
          });
          setError('');
          // モーダルは閉じない（書類アップロードへ続く）
        } else {
          setShowModal(false);
          setForm({ employee_code: '', user_name: '', email: '', role: 'crew', password: '', name_kana: '', birth_date: '', address: '', license_no: '', license_grade: '', license_expiry_date: '', seaman_book_expiry_date: '', is_crew_eligible: true, nationality: '', birth_place: '', seaman_book_no: '', crew_note: '', rank_position: '' });
        }
      } catch (e: any) {
        setError(e.message ?? '登録に失敗しました');
      }
    }
  };

  const doDelete = async (u: User) => {
    try {
      await api.del(`/users/${u.id}`);
      setDeleteConfirm(null);
      fetchUsers();
    } catch (e: any) {
      alert(e.message ?? '削除に失敗しました');
    }
  };

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title">ユーザー管理</h1>
        <div className="flex-row">
          <MasterLinkButton tab="position" title="役職・免許資格などのマスタを開きます" />
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
            <input
              type="text"
              className="form-i"
              placeholder="名前・コード・メールで検索..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ paddingLeft: 30, width: 210 }}
            />
          </div>
          <select
            className="form-i"
            style={{ width: 180 }}
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
          >
            <option value="">全ユーザー</option>
            <option value="crew">⚓ 船員</option>
            <option value="captain">⭐ 船員（船長権限）</option>
            <option value="shore_admin">🏢 陸上管理者</option>
            <option value="system_admin">🔧 システム管理者</option>
          </select>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowExportMenu(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: '1.5px solid #e2e8f0', background: '#fff', color: '#374151', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <Download size={14} /> データ ▾
            </button>
            {showExportMenu && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 200, minWidth: 210, overflow: 'hidden' }}
                onMouseLeave={() => setShowExportMenu(false)}>
                <button onClick={() => { exportCsvFile(filtered.map(userToCsvRow), USER_EXPORT_HEADER, 'ユーザー一覧.csv'); setShowExportMenu(false); }}
                  style={{ width: '100%', padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0f172a', textAlign: 'left' }}>
                  CSVダウンロード (.csv)
                </button>
                <button onClick={() => { exportExcelFile(filtered.map(userToCsvRow), USER_EXPORT_HEADER, 'ユーザー一覧.xlsx').catch(console.error); setShowExportMenu(false); }}
                  style={{ width: '100%', padding: '10px 16px', border: 'none', borderTop: '1px solid #f1f5f9', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0f172a', textAlign: 'left' }}>
                  Excelダウンロード (.xlsx)
                </button>
                <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />
                <button onClick={() => { exportCsvFile(USER_TMPL_ROWS, USER_TMPL_HEADER, 'ユーザー_インポートテンプレート.csv'); setShowExportMenu(false); }}
                  style={{ width: '100%', padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0369a1', textAlign: 'left' }}>
                  テンプレートDL (.csv)
                </button>
                <button onClick={() => { setShowImport(true); setShowExportMenu(false); }}
                  style={{ width: '100%', padding: '10px 16px', border: 'none', borderTop: '1px solid #f1f5f9', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0369a1', textAlign: 'left' }}>
                  CSVインポート
                </button>
              </div>
            )}
          </div>
          <button className="btn-p" onClick={openCreate}>+ 新規登録</button>
        </div>
      </div>

      {!loading && pager.renderBar('top')}
      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
            {searchQuery ? `「${searchQuery}」に一致するユーザーが見つかりません` : 'データがありません'}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('employee_code')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>社員コード {sortIcon('employee_code')}</span>
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('user_name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>名前 {sortIcon('user_name')}</span>
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('user_role')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>ロール {sortIcon('user_role')}</span>
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('email')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>メール {sortIcon('email')}</span>
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('created_at')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>登録日 {sortIcon('created_at')}</span>
                </th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {pager.pageRows.map(u => (
                <tr key={u.id} onClick={() => setDetailUserId(u.id)} style={{ cursor: 'pointer' }} title="クリックで詳細（人材情報）を表示">
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{u.employee_code}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} onClick={e => e.stopPropagation()}>
                      <AvatarEditor
                        entityId={u.id}
                        type="user"
                        imagePath={u.avatar_path}
                        displayName={u.user_name}
                        size={36}
                        onUpdated={(path) => {
                          setUsers(prev => prev.map(x => x.id === u.id ? { ...x, avatar_path: path } : x));
                        }}
                      />
                      <span style={{ fontWeight: 600 }}>{u.user_name}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {u.user_role === 'captain' ? (
                        <>
                          <span style={{ ...ROLE_COLORS.crew, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                            ⚓ 船員
                          </span>
                          <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 20, padding: '3px 9px', fontSize: 11, fontWeight: 700 }}>
                            ⭐ 船長権限（自動）
                          </span>
                        </>
                      ) : (
                        <span style={{ ...(ROLE_COLORS[u.user_role] ?? ROLE_COLORS.crew), borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                          {u.user_role === 'crew' ? '⚓ ' : u.user_role === 'shore_admin' ? '🏢 ' : u.user_role === 'system_admin' ? '🔧 ' : ''}
                          {ROLE_LABELS[u.user_role] ?? u.user_role}
                        </span>
                      )}
                      {u.is_crew_eligible === 1 && !['crew', 'captain'].includes(u.user_role) && (
                        <span style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', borderRadius: 20, padding: '2px 7px', fontWeight: 600 }}>
                          🚢 乗船あり
                        </span>
                      )}
                      {(() => {
                        const today = new Date();
                        const warn90 = new Date(today); warn90.setDate(warn90.getDate() + 90);
                        const warn30 = new Date(today); warn30.setDate(warn30.getDate() + 30);
                        const badges: React.ReactNode[] = [];
                        for (const [date, label] of [[u.license_expiry_date, '免状'], [u.seaman_book_expiry_date, '船員手帳']] as [string|null|undefined, string][]) {
                          if (!date) continue;
                          const exp = new Date(date);
                          if (exp <= today) badges.push(<span key={label} style={{ fontSize: 10, background: '#fee2e2', color: '#dc2626', borderRadius: 20, padding: '2px 7px', fontWeight: 600 }}>⚠ {label}期限切れ</span>);
                          else if (exp <= warn30) badges.push(<span key={label} style={{ fontSize: 10, background: '#fef3c7', color: '#b45309', borderRadius: 20, padding: '2px 7px', fontWeight: 600 }}>⚠ {label}30日以内</span>);
                          else if (exp <= warn90) badges.push(<span key={label} style={{ fontSize: 10, background: '#fff7ed', color: '#c2410c', borderRadius: 20, padding: '2px 7px', fontWeight: 600 }}>📋 {label}90日以内</span>);
                        }
                        return badges;
                      })()}
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{u.email ?? '-'}</td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{u.created_at?.slice(0, 10) ?? '-'}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="flex-row" style={{ gap: 6 }}>
                      <button
                        onClick={() => setDetailUserId(u.id)}
                        style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #e2e8f0', background: 'white', color: '#475569', borderRadius: 6, cursor: 'pointer' }}>
                        詳細
                      </button>
                      <button
                        onClick={() => openEdit(u)}
                        style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #bfdbfe', background: 'white', color: '#2563eb', borderRadius: 6, cursor: 'pointer' }}>
                        編集
                      </button>
                      <span title={u.password_changed_at
                        ? `${new Date(u.password_changed_at + 'Z').toLocaleString('ja-JP')} に ${u.password_changed_by ?? '不明'} が変更`
                        : '変更の記録がありません（記録を残す機能の導入前に設定されたパスワードです）'}
                        style={{ fontSize: 10, color: '#94a3b8', alignSelf: 'center' }}>
                        {u.password_changed_at
                          ? `PW変更 ${new Date(u.password_changed_at + 'Z').toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}(${u.password_changed_by ?? '不明'})`
                          : 'PW変更 記録なし'}
                      </span>
                      {canManagePasswordOf(u.user_role) && (
                      <button
                        onClick={() => void showPassword(u)}
                        disabled={pwLoading === u.id}
                        title={u.has_managed_password
                          ? '管理者が設定したパスワードを表示します（閲覧は監査ログに残ります）'
                          : '本人が自分で変更したパスワードは保管していないため表示できません'}
                        style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #bbf7d0', background: pwShown[u.id] ? '#f0fdf4' : 'white', color: '#15803d', borderRadius: 6, cursor: 'pointer', fontFamily: pwShown[u.id] ? 'Consolas, monospace' : undefined, letterSpacing: pwShown[u.id] ? '0.06em' : undefined, fontWeight: pwShown[u.id] ? 700 : undefined }}>
                        {pwLoading === u.id ? '取得中...' : pwShown[u.id] ? pwShown[u.id] : (u.has_managed_password ? 'パスワード表示' : 'パスワード非保管')}
                      </button>
                      )}
                      {u.user_role === 'shore_admin' && authUser?.user_role === 'system_admin' && (
                        <button
                          onClick={() => setPermTarget(u)}
                          title="機能ごとの編集権限を設定します"
                          style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #ddd6fe', background: 'white', color: '#7c3aed', borderRadius: 6, cursor: 'pointer' }}>
                          権限
                        </button>
                      )}
                      {canDeleteUser(u) && (
                        <button
                          onClick={() => setDeleteConfirm(u)}
                          style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #fecaca', background: 'white', color: '#dc2626', borderRadius: 6, cursor: 'pointer' }}>
                          削除
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {!loading && pager.renderBar('bottom')}

      {/* 新規登録 / 編集モーダル */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 560, width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>
              {editTarget ? 'ユーザー編集' : 'ユーザー登録'}
            </h2>
            {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{error}</div>}

            <div className="form-g">
              <label className="form-l">社員コード {editTarget ? '' : '*'}</label>
              <input
                className="form-i"
                value={form.employee_code}
                onChange={e => setForm(f => ({ ...f, employee_code: e.target.value }))}
                disabled={!!editTarget}
                style={editTarget ? { background: '#f1f5f9', color: '#94a3b8' } : {}}
              />
            </div>
            <div className="form-g">
              <label className="form-l">名前 *</label>
              <input className="form-i" value={form.user_name} onChange={e => setForm(f => ({ ...f, user_name: e.target.value }))} />
            </div>
            {/* アイコン画像（登録・編集時にそのまま設定できる） */}
            <div className="form-g">
              <label className="form-l">アイコン画像</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#f1f5f9', border: '1.5px solid #e2e8f0', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {iconPreview ? <img src={iconPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 22, color: '#cbd5e1' }}>👤</span>}
                </div>
                <label style={{ padding: '7px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#fff', color: '#374151' }}>
                  {iconFile ? '画像を変更' : '画像を選択'}
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => { const f = e.target.files?.[0]; if (f) { setIconFile(f); setIconPreview(URL.createObjectURL(f)); } e.target.value = ''; }} />
                </label>
                {iconFile && <button type="button" onClick={() => { setIconFile(null); setIconPreview(''); }} style={{ border: 'none', background: 'none', color: '#94a3b8', fontSize: 12, cursor: 'pointer' }}>取消</button>}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>登録と同時にアイコンが設定されます（後から変更も可）</div>
            </div>
            <div className="form-g">
              <label className="form-l">メールアドレス</label>
              <input className="form-i" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="example@email.com" />
            </div>
            {/* 区分選択（ラジオ） */}
            <div className="form-g">
              <label className="form-l">区分 *</label>
              {editTarget?.user_role === 'captain' ? (
                <div style={{ padding: '12px 14px', borderRadius: 10, border: '2px solid #fde68a', background: '#fffbeb', fontSize: 13, color: '#92400e' }}>
                  🚢 <strong>船長</strong>（⚓ 船員 + 船長権限）
                  <div style={{ fontSize: 11, color: '#78350f', marginTop: 4 }}>役職は「乗組員配置」で管理されます。区分を変更したい場合は先に配置から外してください。</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {([
                    { value: 'crew',         icon: '⚓', label: '船員',           desc: '乗組員として勤怠管理・乗組員配置の対象。船内の役職（船長・航海士等）は乗組員配置で設定' },
                    { value: 'shore_admin',  icon: '🏢', label: '陸上管理者',      desc: 'システム管理はできないが勤怠・乗組員を管理できる' },
                    // システム管理者は運営管理者のみが任命できる（船会社の管理者には表示しない）
                    ...(authUser?.is_super_admin ? [{ value: 'system_admin', icon: '🔧', label: 'システム管理者', desc: '全設定の管理権限を持つ（運営管理者のみ任命可）' }] : []),
                  ] as const).map(opt => {
                    const isSelected = form.role === opt.value;
                    return (
                      <label key={opt.value}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', padding: '12px 14px', borderRadius: 10, border: `2px solid ${isSelected ? '#0066CC' : '#e2e8f0'}`, background: isSelected ? '#eff6ff' : '#f8fafc', transition: 'all 0.12s' }}>
                        <input type="radio" name="role" value={opt.value}
                          checked={isSelected}
                          onChange={() => setForm(f => ({ ...f, role: opt.value, is_crew_eligible: opt.value === 'crew' ? true : f.is_crew_eligible }))}
                          style={{ marginTop: 3, accentColor: '#0066CC', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: isSelected ? '#0066CC' : '#0f172a' }}>{opt.icon} {opt.label}</div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{opt.desc}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 管理者が乗船する場合のチェック */}
            {(form.role === 'shore_admin' || form.role === 'system_admin') && (
              <div style={{ marginTop: 4, padding: '12px 14px', background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 10 }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={form.is_crew_eligible}
                    onChange={e => setForm(f => ({ ...f, is_crew_eligible: e.target.checked }))}
                    style={{ width: 18, height: 18, accentColor: '#0066CC', cursor: 'pointer', flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>🚢 乗船することがある</div>
                    <div style={{ fontSize: 11, color: '#78350f', marginTop: 2 }}>チェックすると乗組員配置の候補に表示されます。船内役職は配置時に設定してください。</div>
                  </div>
                </label>
              </div>
            )}
            {/* 船員は自動で対象 */}
            {form.role === 'crew' && (
              <div style={{ marginTop: 4, padding: '8px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, fontSize: 12, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                ✓ 船員は乗組員配置・勤怠管理の対象に自動登録されます
              </div>
            )}
            <div style={{ marginTop: 6, padding: '7px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 11, color: '#64748b' }}>
              ℹ 船長権限（他乗組員の勤怠閲覧・アラート確認など）は、乗組員配置で「船長」役職を設定した際に自動付与されます
            </div>
            {/* パスワード: 新規は入力必須。既存は「現在のパスワード（表示）」＋「新しいパスワード（変更時のみ）」。
                他人のパスワードを扱えるのは 陸上管理者・システム管理者・運営管理者だけ（システム管理者の分はシステム管理者以上）。 */}
            {!editTarget ? (
              <div className="form-g">
                <label className="form-l">パスワード *</label>
                <PasswordInput className="form-i" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="6文字以上" />
              </div>
            ) : canManagePasswordOf(editTarget.user_role) ? (
              <div style={{ marginTop: 6, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                <div className="form-l" style={{ marginBottom: 6 }}>現在のパスワード</div>
                <PasswordReveal userId={editTarget.id} targetRole={editTarget.user_role} refreshKey={pwSavedTick}
                  hint="下の「新しいパスワード」に入力して保存すると、新しいパスワードを設定できます。" />
                <div className="form-g" style={{ marginTop: 12, marginBottom: 0 }}>
                  <label className="form-l">新しいパスワード（変更する場合のみ入力）</label>
                  <PasswordInput className="form-i" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="変更しない場合は空欄（6文字以上）" autoComplete="new-password" />
                  <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>
                    保存すると古いパスワードは使えなくなり、ログイン中の端末は次回から新しいパスワードでのログインが必要です。設定した値は上の「表示」で確認できます。
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: 6, padding: '7px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 11, color: '#64748b' }}>
                🔒 このユーザーのパスワードは確認・変更できません（システム管理者のパスワードはシステム管理者か運営管理者だけが扱えます）。
              </div>
            )}

            {/* 証明書用プロフィール */}
            <div style={{ margin: '16px 0 8px', fontSize: 12, fontWeight: 700, color: '#64748b', borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
              証明書用プロフィール（任意）
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-g" style={{ margin: 0 }}>
                <label className="form-l">氏名（ふりがな）</label>
                <input className="form-i" value={form.name_kana} onChange={e => setForm(f => ({ ...f, name_kana: e.target.value }))} placeholder="例: やまだ たろう" />
              </div>
              <div className="form-g" style={{ margin: 0 }}>
                <label className="form-l">生年月日</label>
                <SmartDateInput value={form.birth_date} onChange={v => setForm(f => ({ ...f, birth_date: v }))} baseYear={1990} placeholder="例: 1985/4/1" />
              </div>
              <div className="form-g" style={{ margin: 0, gridColumn: '1 / -1' }}>
                <label className="form-l">住所</label>
                <input className="form-i" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="例: 東京都港区..." />
              </div>
              <div className="form-g" style={{ margin: 0, gridColumn: '1 / -1' }}>
                <label className="form-l">辞令上の役職</label>
                <input className="form-i" list="rank-position-list" value={form.rank_position}
                  onChange={e => setForm(f => ({ ...f, rank_position: e.target.value }))} placeholder="例: 二等航海士" />
                <datalist id="rank-position-list">{positionOptions.map(p => <option key={p} value={p} />)}</datalist>
                <div style={{ fontSize: 11, color: '#78350f', marginTop: 4 }}>
                  会社としての立場（辞令）です。乗船中に執る職は配乗側で設定し、両者が異なる期間は「執職」として乗下船表・配乗シミュレーションでハイライトされます。
                </div>
              </div>
              <div className="form-g" style={{ margin: 0 }}>
                <label className="form-l">海技免状番号</label>
                <input className="form-i" value={form.license_no} onChange={e => setForm(f => ({ ...f, license_no: e.target.value }))} placeholder="例: 第○○号" />
              </div>
              <div className="form-g" style={{ margin: 0 }}>
                <label className="form-l">海技免状の種類</label>
                <input className="form-i" value={form.license_grade} onChange={e => setForm(f => ({ ...f, license_grade: e.target.value }))} placeholder="例: 四級海技士（航海）" />
              </div>
              <div className="form-g" style={{ margin: 0 }}>
                <label className="form-l">海技免状 有効期限</label>
                <SmartDateInput value={form.license_expiry_date} onChange={v => setForm(f => ({ ...f, license_expiry_date: v }))} placeholder="例: 2028/3/31" />
              </div>
              <div className="form-g" style={{ margin: 0 }}>
                <label className="form-l">船員手帳 有効期限</label>
                <SmartDateInput value={form.seaman_book_expiry_date} onChange={v => setForm(f => ({ ...f, seaman_book_expiry_date: v }))} placeholder="例: 2028/3/31" />
              </div>
              <div className="form-g" style={{ margin: 0 }}>
                <label className="form-l">船員手帳番号</label>
                <input className="form-i" value={form.seaman_book_no} onChange={e => setForm(f => ({ ...f, seaman_book_no: e.target.value }))} placeholder="例: 第○○号" />
              </div>
              <div className="form-g" style={{ margin: 0 }}>
                <label className="form-l">国籍</label>
                <input className="form-i" value={form.nationality} onChange={e => setForm(f => ({ ...f, nationality: e.target.value }))} placeholder="例: 日本" />
              </div>
              <div className="form-g" style={{ margin: 0 }}>
                <label className="form-l">出身地</label>
                <input className="form-i" value={form.birth_place} onChange={e => setForm(f => ({ ...f, birth_place: e.target.value }))} placeholder="例: 愛媛県今治市" />
              </div>
            </div>

            {/* 配乗上の注意（人材管理に反映される備考） */}
            <div className="form-g" style={{ marginTop: 12 }}>
              <label className="form-l">配乗上の注意（備考）</label>
              <textarea
                value={form.crew_note}
                onChange={e => setForm(f => ({ ...f, crew_note: e.target.value }))}
                rows={3}
                placeholder="例: 〇〇氏と同船不可 / 機関経験が浅いためベテランと組ませる / 持病あり・服薬中 など"
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1.5px solid #fde68a', background: '#fffbeb', borderRadius: 8, fontSize: 13, color: '#78350f', outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.7 }}
              />
              <div style={{ fontSize: 11, color: '#b45309', marginTop: 3 }}>※ 人材管理ページに反映されます（管理者のみ閲覧可・本人には表示されません）</div>
            </div>

            {/* 顔認証データ（スマホの顔ログイン・顔打刻用の特徴量） */}
            {editTarget && (
              <div style={{ marginTop: 14, padding: '12px 14px', background: '#fff', borderRadius: 10, border: '1.5px solid #e2e8f0' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>🪪 顔認証データ（スマホの顔ログイン）</div>
                {editTarget.face_registered_at ? (
                  faceDeleteConfirm ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: '#991b1b' }}>
                        削除するとこの端末以外も含め顔ログインができなくなります。よろしいですか？
                      </span>
                      <button disabled={faceDeleting}
                        onClick={async () => {
                          setFaceDeleting(true);
                          try {
                            await api.del(`/users/${editTarget.id}/face-data`);
                            await refreshAfterUpload();
                            setFaceDeleteConfirm(false);
                          } catch { alert('顔認証データの削除に失敗しました'); }
                          setFaceDeleting(false);
                        }}
                        style={{ fontSize: 11, color: '#fff', background: '#ef4444', border: 'none', borderRadius: 4, padding: '4px 14px', cursor: faceDeleting ? 'wait' : 'pointer', fontWeight: 700 }}>
                        {faceDeleting ? '削除中...' : '削除する'}
                      </button>
                      <button disabled={faceDeleting} onClick={() => setFaceDeleteConfirm(false)}
                        style={{ fontSize: 11, color: '#64748b', background: 'none', border: '1px solid #cbd5e1', borderRadius: 4, padding: '4px 12px', cursor: 'pointer' }}>取消</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>✅ 登録済み</span>
                      <span style={{ fontSize: 11.5, color: '#94a3b8' }}>最終更新: {editTarget.face_registered_at.slice(0, 16).replace('T', ' ')}</span>
                      <button onClick={() => setFaceDeleteConfirm(true)}
                        style={{ marginLeft: 'auto', fontSize: 11, color: '#ef4444', background: 'none', border: '1px solid #fca5a5', borderRadius: 4, padding: '3px 10px', cursor: 'pointer' }}>
                        顔認証データを削除
                      </button>
                    </div>
                  )
                ) : (
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    未登録（本人が船員アプリの「設定 → 顔の登録」から登録できます）
                  </div>
                )}
                <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 6 }}>
                  ※ 削除するとサーバ保存の顔特徴量とログイン用シークレットが失効し、再登録するまで顔ログイン・顔打刻は使えません。
                </div>
              </div>
            )}

            {/* 書類アップロード（編集モード / 新規作成後の自動移行時） */}
            {editTarget && (
              <div style={{ marginTop: 4 }}>
                {!editTarget.license_pdf_path && !editTarget.license_image_path &&
                 !editTarget.seaman_book_pdf_path && !editTarget.seaman_book_image_path && (
                  <div style={{ fontSize: 12, color: '#0ea5e9', background: '#e0f2fe', borderRadius: 8, padding: '8px 12px', marginBottom: 4 }}>
                    ✅ 登録完了！続けて書類をアップロードできます（任意）
                  </div>
                )}
                <DocSection
                  title="📋 海技免状"
                  userId={editTarget.id}
                  pdfType="pdf"
                  imageType="image"
                  hasPdf={!!editTarget.license_pdf_path}
                  hasImage={!!editTarget.license_image_path}
                  pdfName={editTarget.license_pdf_name}
                  imageName={editTarget.license_image_name}
                  onChanged={refreshAfterUpload}
                />
                <DocSection
                  title="📗 船員手帳"
                  userId={editTarget.id}
                  pdfType="seaman-book-pdf"
                  imageType="seaman-book-image"
                  hasPdf={!!editTarget.seaman_book_pdf_path}
                  hasImage={!!editTarget.seaman_book_image_path}
                  pdfName={editTarget.seaman_book_pdf_name}
                  imageName={editTarget.seaman_book_image_name}
                  onChanged={refreshAfterUpload}
                />
              </div>
            )}

            <div className="flex-row" style={{ justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
              <button className="btn-o" onClick={() => setShowModal(false)}>
                {editTarget ? '閉じる' : 'キャンセル'}
              </button>
              <button className="btn-p" onClick={save}>
                {editTarget ? '更新' : '登録して書類をアップロード →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 削除確認モーダル */}
      {permTarget && (
        <EditPermissionsModal user={permTarget} onClose={() => setPermTarget(null)} />
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: '#dc2626' }}>ユーザー削除</h2>
            <p style={{ fontSize: 14, color: '#374151', marginBottom: 16 }}>
              <strong>{deleteConfirm.user_name}</strong>（{deleteConfirm.employee_code}）を削除しますか？<br />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>※この操作は取り消せません</span>
            </p>
            <div className="flex-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-o" onClick={() => setDeleteConfirm(null)}>キャンセル</button>
              <button
                onClick={() => doDelete(deleteConfirm)}
                style={{ padding: '8px 20px', border: 'none', borderRadius: 8, background: '#dc2626', color: 'white', fontWeight: 600, cursor: 'pointer' }}>
                削除する
              </button>
            </div>
          </div>
        </div>
      )}

      {showImport && (
      <CsvImportModal
        title="ユーザー"
        endpoint="/users/bulk-import"
        bodyKey="users"
        columns={[
          { key: 'employee_code', label: '社員コード',   required: true,  hint: '一意の識別コード' },
          { key: 'user_name',     label: '氏名',         required: true,  hint: '' },
          { key: 'password',      label: 'パスワード',   required: true,  hint: '6文字以上' },
          { key: 'user_role',     label: 'ロール',       required: false, hint: 'crew / captain / shore_admin / system_admin（省略時: crew）' },
          { key: 'email',         label: 'メール',       required: false, hint: '' },
        ]}
        templateRows={[
          ['EMP001', '山田 太郎', 'password123', 'crew',        'yamada@example.com'],
          ['EMP002', '鈴木 次郎', 'password123', 'captain',     ''],
          ['EMP003', '田中 花子', 'password123', 'shore_admin', 'tanaka@example.com'],
        ]}
        onClose={() => setShowImport(false)}
        onComplete={fetchUsers}
      />
      )}

      {/* 個人詳細ドロワー（人材管理と共通） */}
      {detailUserId && (
        <TalentDetailDrawer userId={detailUserId} onClose={() => setDetailUserId(null)} onChanged={fetchUsers} />
      )}
    </div>
  );
};

export default UsersPage;
